import { useCallback, useEffect, useRef, useState } from "react";

interface Props {
  urls: string[];
  alt: string;
  intervalMs?: number;
  /**
   * When true, tap/keyboard on the cover advances to the next photo.
   * Defaults to false so the component is safe inside a parent <Link>
   * (a click on the image navigates to the link target instead of
   * cycling the carousel).
   */
  tapToAdvance?: boolean;
}

export function RotatingCover({ urls, alt, intervalMs = 3500, tapToAdvance = false }: Props) {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    if (urls.length <= 1) return;
    timerRef.current = window.setInterval(() => {
      setIdx((i) => (i + 1) % urls.length);
    }, intervalMs);
  }, [urls.length, intervalMs, clearTimer]);

  useEffect(() => {
    setIdx(0);
    startTimer();
    return clearTimer;
  }, [urls, startTimer, clearTimer]);

  const advance = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      if (urls.length <= 1) return;
      e.preventDefault();
      e.stopPropagation();
      setIdx((i) => (i + 1) % urls.length);
      startTimer();
    },
    [urls.length, startTimer],
  );

  if (urls.length === 0) return null;

  const interactive = tapToAdvance && urls.length > 1;

  return (
    <div
      className={`relative h-full w-full overflow-hidden ${interactive ? "cursor-pointer md:hidden-cursor" : ""}`}
      onClick={interactive ? advance : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " " || e.key === "ArrowRight") advance(e);
            }
          : undefined
      }
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `${alt} – klepnutím zobrazíte další fotku` : undefined}
    >
      {urls.map((url, i) => {
        const active = i === idx;
        return (
          <img
            key={url}
            src={url}
            alt={i === 0 ? alt : ""}
            loading={i === 0 ? "lazy" : "eager"}
            decoding="async"
            fetchPriority={i === 0 ? "high" : "low"}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            width={800}
            height={800}
            style={{
              opacity: active ? 1 : 0,
              transform: "translate3d(0,0,0)",
              willChange: "opacity",
              backfaceVisibility: "hidden",
              objectPosition: "center",
            }}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ease-out motion-reduce:transition-none"
            aria-hidden={active ? undefined : true}
            draggable={false}
          />
        );
      })}
      {interactive && (
        <div className="pointer-events-none absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1">
          {urls.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-1.5 rounded-full transition-all ${
                i === idx ? "bg-white w-4" : "bg-white/60"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
